/**
 * ItemV1 (P0-4b deliverable 3): an item card, stored in `rules_record.data`
 * (kind item). `text` is table-safe; `gmText` is for the GM only, for
 * example a curse. Frozen by 1.0b stage 2 (`DND5E_SCHEMA_STATUS`, `ITEM_SCHEMA_VERSION`): from now
 * on a change is additive only, through a contract steward round. `category` stays an open
 * string; `ITEM_CATEGORIES` lists the known values for the catalogue's filter.
 */
import { AcEntry, allDistinct, CardSourceMeta, DamagePart, DamageTraitGroup, Dice, MoneyEntry, Rarity, RulesVersion, Size, Text, WeaponProperty, } from './common.js';
import { z } from './zod.js';
/** dnd5e 5.x's weapon types. */
export const WEAPON_CATEGORIES = [
    'simpleM',
    'simpleR',
    'martialM',
    'martialR',
    'natural',
    'improv',
    'siege',
];
export const WeaponStats = z
    .strictObject({
    category: z.enum(WEAPON_CATEGORIES),
    /** Whether the weapon is a martial weapon; it agrees with `category`. */
    martial: z.boolean(),
    damage: DamagePart.nullable(),
    versatile: DamagePart.nullable(),
    properties: z.array(WeaponProperty).refine((list) => allDistinct(list), {
        message: 'Each property appears once',
    }),
    range: z.strictObject({
        normal: z.number().int().min(0).nullable(),
        long: z.number().int().min(0).nullable(),
        reach: z.number().int().min(0).nullable(),
    }),
})
    .refine((w) => w.martial === w.category.startsWith('martial'), {
    message: 'martial agrees with the category',
    path: ['martial'],
})
    .refine((w) => w.properties.includes('ver') === (w.versatile !== null), {
    message: 'A versatile weapon, and only one, has versatile damage',
    path: ['versatile'],
});
/** dnd5e 5.x's armor types. */
export const ARMOR_CATEGORIES = ['light', 'medium', 'heavy', 'shield', 'natural'];
export const ArmorStats = z.strictObject({
    category: z.enum(ARMOR_CATEGORIES),
    baseAc: z.number().int().min(0),
    /** Whether the Dexterity modifier is added. */
    addDex: z.boolean(),
    /** The most Dexterity adds; null is no cap. */
    dexCap: z.number().int().min(0).nullable(),
    /** The Strength score required. */
    strength: z.number().int().min(1).nullable(),
    stealthDisadvantage: z.boolean(),
});
/** An object's statistics (DMG chapter 8): armor class, hit points and damage traits. */
export const ObjectStats = z.strictObject({
    size: Size.nullable(),
    armorClass: AcEntry.nullable(),
    hitPoints: z.number().int().min(0).nullable(),
    hitDice: Dice.nullable(),
    damageImmunities: z.array(DamageTraitGroup),
    damageResistances: z.array(DamageTraitGroup),
    damageVulnerabilities: z.array(DamageTraitGroup),
});
/**
 * The known values of `ItemV1.category`, for the catalogue's filter (1.0b deliverable 16): Open5e's
 * item category keys, as the committed SRD snapshot lists them (`ItemCategory.json`). The field
 * stays an open string, so a homebrew category still parses.
 */
export const ITEM_CATEGORIES = [
    'adventuring-gear',
    'ammunition',
    'armor',
    'art',
    'gem',
    'jewelry',
    'land-vehicle',
    'mount',
    'poison',
    'potion',
    'ring',
    'rod',
    'scroll',
    'service',
    'shield',
    'staff',
    'tools',
    'trade-good',
    'wand',
    'waterborne-vehicle',
    'weapon',
    'wondrous-item',
];
export const ItemV1 = z.strictObject({
    schemaVersion: z.literal(1),
    source: CardSourceMeta,
    rules: RulesVersion.nullable(),
    name: Text,
    /** The item category key: "weapon", "armor", "wondrous-item", "potion". */
    category: Text,
    /** null for a mundane item. */
    rarity: Rarity.nullable(),
    attunement: z
        .strictObject({
        required: z.boolean(),
        /** "by a cleric or paladin". */
        requirement: z.string().nullable(),
    })
        .refine((a) => a.required || a.requirement === null, {
        message: 'Only an item that requires attunement has a requirement',
        path: ['requirement'],
    }),
    weapon: WeaponStats.nullable(),
    armor: ArmorStats.nullable(),
    object: ObjectStats.nullable(),
    /** In pounds. */
    weight: z.number().min(0).nullable(),
    cost: MoneyEntry.nullable(),
    /** Table-safe CommonMark. */
    text: z.string(),
    /** GM-only text, for example a curse; never rendered for the table. */
    gmText: z.string().nullable(),
});
//# sourceMappingURL=item.js.map